import { useNavigate } from "react-router"
import { SystemDescriptions } from "src/common/descriptions"
import { PageCard } from "src/common/styles/styles"
import { Urls } from "src/common/urls"
import { SearchableTable } from "src/components/SearchableTable"
import useUserPermission from "src/hooks/useUserPermissions"
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { ServiceContract } from "src/common/models/contract"
import { Button, Space, Tooltip } from "antd"
import { ProfileOutlined, EditOutlined } from '@ant-design/icons'
import { ColumnsType } from "antd/es/table"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { useEffect, useState } from "react"
import { getServiceContractsApi } from "../state/action"
import { GET_CONTRACT_BY_ID_API, GET_SERVICE_CONTRACTS_API } from "../state/actionTypes"
import { Loader } from "src/components/Loader"
import { CategorySelector } from "../CreateServiceContract/ServicesSection/CategprySelector"
import { ServiceCategory } from "src/common/models/serviceCategory"
import { getServiceCategoriesApi } from "src/features/Service/state/action"
import { USER_SCOPES } from "src/hooks/UserPermission"
import { ContractView } from "src/features/PayableContract/PayableContracts"

export type ReduxProps = {
    serviceContracts?: ServiceContract[],
    isGettingContracts: boolean,
    currentContract?: Partial<ServiceContract>
    isGettingContract: boolean
}

export const ServiceContracts = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { validateUserScopeForPage } = useUserPermission()

    const { isUserAllowedToPerformAdminActions: userAdminActions } = useUserPermission()

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        serviceContracts: state.serviceContract.serviceContracts || [],
        isGettingContracts: serviceLoading(state, [GET_SERVICE_CONTRACTS_API]),
        currentContract: state.serviceContract.currentContract,
        isGettingContract: serviceLoading(state, [GET_CONTRACT_BY_ID_API]),
    }))

    const [contracts, setContracts] = useState<ServiceContract[]>(reduxProps.serviceContracts || [])
    const [contractsView, setContractsView] = useState(ContractView.ACTIVE)

    useEffect(() => {
        validateUserScopeForPage(USER_SCOPES.SERVICE_CONTRACT_READ)
        dispatch(getServiceContractsApi({
            isActive: true,
        }))
        dispatch(getServiceCategoriesApi())
    }, [])

    useEffect(() => {
        if (reduxProps.serviceContracts) {
            setContracts(reduxProps.serviceContracts)
        } else {
            setContracts([])
        }
    }, [reduxProps.serviceContracts, contractsView])

    const descriptions = SystemDescriptions.PAGES.SERVICE_CONTRACT.SERVICE_CONTRACTS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const handleActionClick = () => {
        navigate(Urls.FRONTEND.SERVICE_CONTRACT.CREATE)
    }

    const tblColumns: ColumnsType<ServiceContract> = [
        ...TABLE_COLUMNS,
        {
            title: descriptions.TABLE_COLUMNS.ACTIONS,
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceContract) => {
                const handleViewClick = (contractId: number) => {
                    navigate(Urls.FRONTEND.SERVICE_CONTRACT.DETAIL_PAGE(contractId))
                }

                const handleEditClick = () => {
                    navigate(Urls.FRONTEND.SERVICE_CONTRACT.EDIT_PAGE(record.id!))
                }

                return <Space size="middle">
                    <Tooltip title={commonDescriptions.ACTIONS.VIEW} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.id!)}
                        />
                    </Tooltip>
                    <Tooltip title={commonDescriptions.ACTIONS.EDIT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<EditOutlined />}
                            onClick={handleEditClick}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingContract}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    const renderExtra = () => {
        return <Button
            color={contractsView !== ContractView.ACTIVE ? "default" : "danger"}
            variant="filled"
            size="small"
            onClick={() => {
                dispatch(getServiceContractsApi({
                    isActive: contractsView !== ContractView.ACTIVE ? true : false,
                }))

                setContractsView(
                    contractsView === ContractView.ACTIVE
                        ? ContractView.INACTIVE
                        : ContractView.ACTIVE
                )
            }}
        >
            {
                contractsView === ContractView.ACTIVE
                    ? descriptions.VIEW_INACTIVES
                    : descriptions.VIEW_ACTIVES
            }
        </Button>
    }

    return (
        <PageCard
            size="small"
            title={descriptions.TITLE}
            extra={renderExtra()}
        >
            {
                contractsView === ContractView.ACTIVE &&
                <div style={{ marginBottom: 12, }}>
                    <CategorySelector
                        text={descriptions.TABLE.LOADING_DATA}
                        onAccept={function (category: ServiceCategory): void {
                            let newContracts: ServiceContract[] = []

                            if (category.id !== 0) {
                                reduxProps.serviceContracts?.forEach(contract => {
                                    const arrayOfIds: string[] = contract.categoriesInDetailIds.split(',')
                                    if (arrayOfIds.includes(category.id!.toString())) {
                                        newContracts.push(contract)
                                    }
                                })
                            } else {
                                newContracts = reduxProps.serviceContracts || []
                            }

                            setContracts(newContracts)
                        }}
                        isEmbed
                    />
                </div>
            }
            <SearchableTable
                showSearch
                showActionButton={userAdminActions}
                actionButtonLabel={descriptions.TABLE.ACTION_BUTTON}
                actionButtonOnClick={handleActionClick}
                showLoader={reduxProps.isGettingContracts}
                items={contracts || []}
                tableColumns={tblColumns}
                searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                summaryColumns={summaryColumns}
            />
            {renderLoader()}
        </PageCard>
    )
}
